// Page de suivi de commande - Design Licorne Premium
// Timeline visuelle avec statuts en temps réel

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, Truck, CheckCircle2, Clock, XCircle, MapPin,
  ArrowLeft, CreditCard, Box, Home, Phone, Mail,
  Copy, ExternalLink, RefreshCw, AlertTriangle, Sparkles,
  ChevronRight, Calendar, User, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import { ordersAPI } from '../../services/buyerAPI';

// Définition des étapes de la timeline
const TIMELINE_STEPS = [
  { 
    id: 'pending', 
    label: 'Commande reçue', 
    icon: Package,
    description: 'Ta commande a été enregistrée'
  },
  { 
    id: 'paid', 
    label: 'Paiement validé', 
    icon: CreditCard,
    description: 'Le paiement a été confirmé'
  },
  { 
    id: 'confirmed', 
    label: 'Confirmée', 
    icon: CheckCircle2,
    description: 'Le vendeur prépare ta commande'
  },
  { 
    id: 'preparing', 
    label: 'En préparation', 
    icon: Box,
    description: 'Tes articles sont en cours d\'emballage'
  },
  { 
    id: 'shipped', 
    label: 'Expédiée', 
    icon: Truck,
    description: 'Ton colis est en route'
  },
  { 
    id: 'delivered', 
    label: 'Livrée', 
    icon: Home,
    description: 'Ta commande a été livrée'
  }
];

// Mapping statut -> index dans la timeline
const STATUS_INDEX = {
  pending: 0,
  paid: 1,
  confirmed: 2,
  preparing: 3,
  shipped: 4,
  delivered: 5,
  cancelled: -1,
  refunded: -1
};

export default function SuiviCommandePage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const data = await ordersAPI.getById(orderId);
      setOrder(data);
    } catch (e) {
      setError('Commande introuvable');
      toast.error('Impossible de charger la commande');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price || 0);
  };

  // Index actuel dans la timeline
  const currentStepIndex = order ? STATUS_INDEX[order.status] : 0;
  const isCancelled = order?.status === 'cancelled' || order?.status === 'refunded';

  // Extraire tous les articles
  const allItems = order?.sellers?.flatMap(s => 
    s.items.map(item => ({ ...item, seller_name: s.seller_name }))
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <div className="flex items-center justify-center py-32">
          <div className="w-12 h-12 border-4 border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <UnifiedHeader />
        <MainNavigation />
        <div className="h-24 md:hidden" />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <XCircle size={64} className="mx-auto text-red-300 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Commande introuvable</h1>
          <p className="text-gray-500 mb-6">Cette commande n'existe pas ou tu n'y as pas accès.</p>
          <Link to="/mes-commandes">
            <Button className="bg-[#F97316] hover:bg-[#EA580C]">
              Voir mes commandes
            </Button>
          </Link>
        </main>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <UnifiedHeader />
      <MainNavigation />
      <div className="h-24 md:hidden" />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/mes-commandes')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Suivi de commande</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                <span className="font-mono">{order.order_number}</span>
                <button 
                  onClick={() => copyToClipboard(order.order_number, 'Numéro')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadOrder}>
              <RefreshCw size={16} className="mr-1" />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Alerte mode préouverture */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Mode Préouverture</p>
              <p className="text-xs text-amber-700 mt-1">
                Cette commande est une simulation. Le tracking réel sera activé prochainement.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Timeline de suivi */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h2 className="font-bold text-lg text-gray-900 mb-6">Statut de la commande</h2>

          {isCancelled ? (
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle size={24} className="text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-red-800">
                  Commande {order.status === 'cancelled' ? 'annulée' : 'remboursée'}
                </p>
                <p className="text-sm text-red-600">
                  {order.status === 'cancelled' 
                    ? 'Cette commande a été annulée.' 
                    : 'Cette commande a été remboursée.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Ligne de connexion */}
              <div className="absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200" />
              <div 
                className="absolute left-6 top-6 w-0.5 bg-[#F97316] transition-all duration-500"
                style={{ 
                  height: `${Math.min(100, (currentStepIndex / (TIMELINE_STEPS.length - 1)) * 100)}%` 
                }}
              />

              {/* Étapes */}
              <div className="space-y-6">
                {TIMELINE_STEPS.map((step, idx) => {
                  const isCompleted = idx <= currentStepIndex;
                  const isCurrent = idx === currentStepIndex;
                  const StepIcon = step.icon;

                  return (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className={`relative flex items-start gap-4 ${
                        isCompleted ? '' : 'opacity-50'
                      }`}
                    >
                      {/* Icône */}
                      <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isCurrent 
                          ? 'bg-[#F97316] text-white ring-4 ring-[#F97316]/20' 
                          : isCompleted 
                            ? 'bg-[#F97316] text-white' 
                            : 'bg-gray-200 text-gray-400'
                      }`}>
                        {isCurrent && (
                          <span className="absolute inset-0 rounded-full bg-[#F97316] animate-ping opacity-30" />
                        )}
                        <StepIcon size={20} />
                      </div>

                      {/* Contenu */}
                      <div className="flex-1 pt-2">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                            {step.label}
                          </h3>
                          {isCurrent && (
                            <span className="px-2 py-0.5 bg-[#F97316]/10 text-[#F97316] text-xs font-medium rounded-full">
                              En cours
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${isCompleted ? 'text-gray-500' : 'text-gray-400'}`}>
                          {step.description}
                        </p>
                        
                        {/* Date si step complétée */}
                        {isCompleted && idx <= currentStepIndex && (
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Calendar size={12} />
                            {idx === 0 ? formatDate(order.created_at) :
                             idx === 1 ? formatDate(order.paid_at) :
                             idx === 4 ? formatDate(order.sellers?.[0]?.shipped_at) :
                             idx === 5 ? formatDate(order.sellers?.[0]?.delivered_at) :
                             formatDate(order.updated_at)}
                          </p>
                        )}

                        {/* Tracking number si expédié */}
                        {step.id === 'shipped' && isCompleted && order.sellers?.[0]?.tracking_number && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-gray-500">Numéro de suivi</p>
                                <p className="font-mono text-sm font-medium">{order.sellers[0].tracking_number}</p>
                              </div>
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => copyToClipboard(order.sellers[0].tracking_number, 'Tracking')}
                                  className="p-2 hover:bg-gray-200 rounded-lg text-gray-500"
                                >
                                  <Copy size={16} />
                                </button>
                                <a 
                                  href={`https://www.laposte.fr/outils/suivre-vos-envois?code=${order.sellers[0].tracking_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-gray-200 rounded-lg text-gray-500"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              </div>
                            </div>
                            {order.sellers[0].carrier && (
                              <p className="text-xs text-gray-400 mt-1">
                                Transporteur : {order.sellers[0].carrier}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Détails des articles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <h2 className="font-bold text-lg text-gray-900 mb-4">
              Articles ({allItems.length})
            </h2>
            <div className="space-y-4">
              {allItems.map((item, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                    {item.product_image ? (
                      <img 
                        src={item.product_image} 
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={24} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                    <p className="text-sm text-gray-500">Vendu par {item.seller_name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm text-gray-500">Qté : {item.quantity}</span>
                      <span className="font-semibold text-[#F97316]">{formatPrice(item.total_price)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totaux */}
            <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sous-total</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Livraison</span>
                <span>{order.shipping_total === 0 ? 'Offerte' : formatPrice(order.shipping_total)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Réduction</span>
                  <span>-{formatPrice(order.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-100">
                <span>Total</span>
                <span className="text-[#F97316]">{formatPrice(order.total)}</span>
              </div>
            </div>
          </motion.div>

          {/* Adresse de livraison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm p-6"
          >
            <h2 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-[#F97316]" />
              Adresse de livraison
            </h2>
            
            {order.shipping_address && (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User size={18} className="text-gray-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">{order.shipping_address.full_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Home size={18} className="text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-gray-700">{order.shipping_address.address_line1}</p>
                    {order.shipping_address.address_line2 && (
                      <p className="text-gray-500">{order.shipping_address.address_line2}</p>
                    )}
                    <p className="text-gray-700">
                      {order.shipping_address.postal_code} {order.shipping_address.city}
                    </p>
                    <p className="text-gray-500">{order.shipping_address.country}</p>
                  </div>
                </div>
                {order.shipping_address.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-gray-400" />
                    <p className="text-gray-700">{order.shipping_address.phone}</p>
                  </div>
                )}
                {order.shipping_address.instructions && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Instructions de livraison</p>
                    <p className="text-sm text-gray-700">{order.shipping_address.instructions}</p>
                  </div>
                )}
              </div>
            )}

            {/* Mode de livraison */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center">
                  <Truck size={20} className="text-[#F97316]" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {order.delivery_method === 'express' ? 'Livraison Express' :
                     order.delivery_method === 'pickup' ? 'Retrait en point relais' :
                     'Livraison Standard'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.delivery_method === 'express' ? '24-48h' :
                     order.delivery_method === 'pickup' ? 'À récupérer en point relais' :
                     '3-5 jours ouvrés'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3"
        >
          <Link to="/mes-commandes" className="flex-1">
            <Button variant="outline" className="w-full">
              <ArrowLeft size={18} className="mr-2" />
              Retour aux commandes
            </Button>
          </Link>
          <Link to="/service-client" className="flex-1">
            <Button variant="outline" className="w-full">
              <FileText size={18} className="mr-2" />
              Besoin d'aide ?
            </Button>
          </Link>
          <Link to="/produits" className="flex-1">
            <Button className="w-full bg-[#F97316] hover:bg-[#EA580C]">
              <Sparkles size={18} className="mr-2" />
              Continuer mes achats
            </Button>
          </Link>
        </motion.div>
      </main>

      
      <div className="h-24 lg:hidden" />
    </div>
  );
}
